import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { format, parseISO } from 'date-fns';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES, getCurrencySymbol, formatCompactNumber } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';

export const RefundSpikeAnalysisChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance, currencySettings, isLoading } = useBoardChartData();
    const currencySymbol = getCurrencySymbol(currencySettings?.default);

    const chartData = useMemo(() => {
        if (!finance?.dailyBreakdown) return [];
        const sortedDates = Object.keys(finance.dailyBreakdown!).sort();
        return sortedDates.map((dateStr, idx) => {
            const m = finance.dailyBreakdown![dateStr].metrics;
            const refundDateIdx = Math.min(idx + 14, sortedDates.length - 1);
            const refundDate = sortedDates[refundDateIdx];
            const refundMetrics = finance.dailyBreakdown![refundDate]?.metrics;
            return {
                date: dateStr,
                dateLabel: format(parseISO(dateStr), 'MMM dd'),
                orders: m.orders || 0,
                refunds: refundMetrics?.returns || 0,
            };
        });
    }, [finance]);

    const isEmpty = !isLoading && chartData.length === 0;
    useSetChartMetadata({ isEmpty });

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 p-2">
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={chartData} margin={CHART_STYLES.margin.withAxis}>
                        <CartesianGrid strokeDasharray={CHART_STYLES.grid.strokeDasharray} vertical={false} stroke={CHART_STYLES.grid.stroke} opacity={CHART_STYLES.grid.opacity} />
                        <XAxis dataKey="dateLabel" {...CHART_STYLES.axis} tick={{ ...CHART_STYLES.axis.tick, fontSize: 10 }} interval="preserveStartEnd" />
                        <YAxis yAxisId="left" {...CHART_STYLES.axis} tick={{ ...CHART_STYLES.axis.tick, fontSize: 10 }} width={40} />
                        <YAxis yAxisId="right" orientation="right" {...CHART_STYLES.axis} tickFormatter={(v) => formatCompactNumber(v, currencySymbol)} tick={{ ...CHART_STYLES.axis.tick, fontSize: 10 }} width={50} />
                        <Tooltip contentStyle={CHART_STYLES.tooltip.contentStyle} />
                        <Legend wrapperStyle={{ fontSize: '10px' }} />
                        <Bar yAxisId="left" dataKey="orders" name={t('charts.orders', 'Orders') as string} fill={CHART_COLORS.primary} opacity={0.6} radius={[3, 3, 0, 0]} maxBarSize={20} />
                        <Line yAxisId="right" dataKey="refunds" name={t('charts.refunds14dLater', 'Refunds (+14d)') as string} stroke={CHART_COLORS.danger} strokeWidth={2} dot={false} />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
